import uniqBy from 'lodash-es/uniqBy';
import cuid from 'cuid';
import {
  ChannelMessageAttachmentType,
  ChannelViewChannelMemberFragment,
  ChannelViewClinicPetFragment,
  ChannelViewClinicPetParentFragment,
  SmsNotificationDeliveryErrorType,
  SmsNotificationProvider,
} from 'shared/types/graphql';
import { IAttachmentOption } from '../interfaces/IAttachmentOption';

export const createUniqueId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const convertFileToAttachment = (file: File): IAttachmentOption => {
  let attachmentType = ChannelMessageAttachmentType.File;

  if (file.type.includes('image')) {
    attachmentType = ChannelMessageAttachmentType.Image;
  } else if (file.type.includes('video')) {
    attachmentType = ChannelMessageAttachmentType.Video;
  }

  return {
    id: createUniqueId(),
    value: {
      id: cuid(),
      attachmentType,
      filename: file.name,
    },
    file,
  };
};

export const getChannelParticipants = (
  channelMembers?: ChannelViewChannelMemberFragment[],
): {
  channelPetParents: ChannelViewClinicPetParentFragment[];
  channelPetParentIds: string[];
  allChannelPets: ChannelViewClinicPetFragment[];
  petParentChannelMembers: ChannelViewChannelMemberFragment[];
} => {
  const channelPetParents: ChannelViewClinicPetParentFragment[] = [];
  const channelPetsUnfiltered: ChannelViewClinicPetFragment[] = [];
  let petParentChannelMembers: ChannelViewChannelMemberFragment[] = [];

  channelMembers?.forEach(({ removedAt, clinicPetParent }) => {
    if (!!clinicPetParent && !removedAt) {
      channelPetParents.push(clinicPetParent);
      clinicPetParent.pets?.forEach((p) => channelPetsUnfiltered.push(p));
    }
  });
  const channelPetParentIds = channelPetParents.map(({ id }) => id);

  const allChannelPets = uniqBy(channelPetsUnfiltered, (pet) => pet.id).filter((pet) => !pet.isDeceased);

  if (channelMembers)
    petParentChannelMembers = uniqBy(
      channelMembers?.filter((member) => member.clinicPetParent),
      (member) => member?.clinicPetParent?.id,
    );

  return { channelPetParents, allChannelPets, channelPetParentIds, petParentChannelMembers };
};

export const getAttachmentPreviewLabel = (attachmentType: ChannelMessageAttachmentType | undefined): string => {
  switch (attachmentType) {
    case ChannelMessageAttachmentType.ServiceReminderFormSubmission:
      return '(Service Reminder Form Submission)';
    case ChannelMessageAttachmentType.FormRequest:
      return '(Form attachment)';
    case ChannelMessageAttachmentType.Invoice:
      return '(Payment attachment)';
    case ChannelMessageAttachmentType.Image:
      return '(Image attachment)';
    case ChannelMessageAttachmentType.Video:
      return '(Video attachment)';
    case ChannelMessageAttachmentType.File:
      return '(File attachment)';
    case ChannelMessageAttachmentType.CallRequest:
      return '(Video call request)';
    case ChannelMessageAttachmentType.AppointmentArrival:
    case ChannelMessageAttachmentType.AppointmentConfirmation:
    case ChannelMessageAttachmentType.AppointmentReminder:
      return '(Appointment reminder attachment)';
    case ChannelMessageAttachmentType.WidgetRequest:
      return '(Website Widget request)';
    case ChannelMessageAttachmentType.PetParentRequest:
      return '(Pet portal request)';
    case ChannelMessageAttachmentType.AutomationRun:
      return '(Automation)';
    default:
      return '(Attachment)';
  }
};

export const getSmsNotificationMessageFromErrorType = (
  errorType: SmsNotificationDeliveryErrorType,
  provider: SmsNotificationProvider,
): string => {
  switch (errorType) {
    case SmsNotificationDeliveryErrorType.ConnectionError:
      return 'Connection error';
    case SmsNotificationDeliveryErrorType.CannotReceiveMessageType:
      return 'The phone number is a landline or is otherwise unreachable by text message';
    case SmsNotificationDeliveryErrorType.InvalidContactInfo:
      return 'Invalid contact info';
    case SmsNotificationDeliveryErrorType.MessageFilteredByCarrier:
      return `The client's carrier flagged the content of this text.${
        provider === SmsNotificationProvider.Bandwidth ? ' Message delivered in Pet Portal Instead' : ''
      }`;
    case SmsNotificationDeliveryErrorType.NotificationRefused:
      return 'The recipient opted out of receiving text messages from this number';
    case SmsNotificationDeliveryErrorType.ErrorSendingMessageToProvider:
      return `${provider} not accepting messages`;
    case SmsNotificationDeliveryErrorType.Unknown:
    default:
      return 'Unknown';
  }
};

export const toPossessiveWord = (name: string): string => {
  return `${name}${name.charAt(name.length - 1).toLowerCase() === 's' ? "'" : "'s"}`;
};
